// `/` — public landing for athrelaris.com.
//
// Three states:
// - Authenticated + bond exists → redirect to /home (the bond is the
//   user's home).
// - Authenticated + no bond → redirect to /awakening (resume ritual).
// - Not authenticated → render the landing.
//
// The landing's only function is to take a visitor (typically from
// the Instagram bio link) and walk them through the lore beats to
// the Awakening door. No nav, no menu, no marketing widgets — just
// trailer-paced atmosphere.

import { Link, createFileRoute, redirect } from '@tanstack/react-router';
import { useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { getMyBond } from '../lib/api';
import { getSessionSafe } from '../lib/auth-client';
import { isLandingOnly } from '../lib/landing-mode';

export const Route = createFileRoute('/')({
  beforeLoad: async () => {
    // The `/` route is the only place the two-hostname split shows
    // up explicitly in routing logic. Behavior diverges by host:
    //
    //   - athrelaris.com (landing-only host) → render the landing
    //     component. No session probe; the public site doesn't need
    //     to know about auth.
    //   - app.athrelaris.com (full-app host) → never show the landing.
    //     Route into the app proper: /home if a bond exists, /awakening
    //     if logged in but no bond yet, /login otherwise. Tolerates an
    //     unreachable API (local dev without apps/api running) by
    //     falling through to /login — there's nothing useful to show
    //     on the app host without backend connectivity.
    if (isLandingOnly()) return;

    const session = await getSessionSafe();
    if (!session?.session) {
      throw redirect({ to: '/login' });
    }
    const bond = await getMyBond();
    throw redirect({ to: bond ? '/home' : '/awakening' });
  },
  component: LandingPage,
});

interface Beat {
  /** Public path under apps/web/public/landing/ */
  imageSrc: string;
  /** i18n key under landing.beats — drives line/body/alt. */
  key: 'earth' | 'water' | 'fire' | 'air';
}

// Order: world → unsaid memory → bond → personal call. Earth opens
// the world, Water carries the silent things, Fire forms the bond,
// Air closes with the invitation aimed at the visitor.
const BEATS: Beat[] = [
  { imageSrc: '/landing/earth.png', key: 'earth' },
  { imageSrc: '/landing/water.png', key: 'water' },
  { imageSrc: '/landing/fire.png', key: 'fire' },
  { imageSrc: '/landing/air.png', key: 'air' },
];

function LandingPage() {
  return (
    <div className="-mx-6 -mt-10 sm:-mx-10 sm:-mt-14 lg:-mx-12 lg:-mt-16">
      <Hero />
      {BEATS.map((b, i) => (
        <BeatSection key={b.key} beat={b} index={i} />
      ))}
      <DoorCta />
      <LandingFooter />
    </div>
  );
}

function Hero() {
  const { t } = useTranslation();
  // The hero title carries an explicit \n that we want to render as a
  // line break — split on \n and emit <br/> rather than feed raw HTML
  // through dangerouslySetInnerHTML.
  const titleLines = t('landing.heroTitle').split('\n');
  return (
    <section className="relative flex min-h-[65vh] flex-col items-center justify-center overflow-hidden px-6 py-12 text-center sm:px-10 sm:py-16">
      <BackgroundShards />
      <div className="relative z-10 max-w-3xl">
        <p className="font-display text-[0.62rem] text-ash-500 uppercase tracking-[0.5em] sm:text-xs lg:text-sm">
          {t('landing.eyebrow')}
        </p>
        <h1
          className="mt-6 font-display font-medium text-ash-50 text-5xl leading-tight tracking-wide sm:text-7xl lg:text-[6rem] lg:leading-[1.05]"
          style={{ fontWeight: 500 }}
        >
          {titleLines.map((line, i) => (
            <span key={line}>
              {line}
              {i < titleLines.length - 1 && <br />}
            </span>
          ))}
        </h1>
        <p
          className="mt-8 font-body text-ash-400 text-base italic leading-relaxed sm:text-lg lg:mt-10 lg:text-xl"
          style={{ animation: 'fadeUp 1.6s ease-out 0.6s both' }}
        >
          {t('landing.heroBody')}
        </p>
        {/* Both the readOn divider and the codex link below were
         * inline-level (inline-flex and <a>), so on wider locales
         * (ES strings run longer) they could flow on the same line
         * and visually collide. Forcing each to a block-level box
         * with w-fit keeps them sized to their content but stacked
         * vertically on every breakpoint. */}
        <div
          className="mt-10 flex w-fit items-center gap-3 font-display text-[0.7rem] text-ash-500 uppercase tracking-[0.4em] sm:text-xs lg:mt-12 lg:text-sm"
          style={{ animation: 'fadeUp 1.4s ease-out 1.2s both' }}
        >
          <span aria-hidden="true" className="h-px w-10 bg-ash-700" />
          <span>{t('landing.readOn')}</span>
          <span aria-hidden="true" className="h-px w-10 bg-ash-700" />
        </div>
        {/* Hero-level secondary CTA so visitors who bounce before the
         * door section (Reel 1 T+4d showed 3s avg watch — most leave
         * fast) still get the codex path. Italic prose-weight typography
         * (not button) keeps the hero from feeling commercial; ash-300
         * keeps it legible without competing with the title. */}
        <Link
          to="/codex"
          className="mt-8 block w-fit font-body text-ash-300 text-base italic transition hover:text-ash-50 sm:text-lg lg:mt-10 lg:text-xl"
          style={{ animation: 'fadeUp 1.4s ease-out 1.5s both' }}
        >
          {t('landing.heroCodexHint')} →
        </Link>
      </div>
    </section>
  );
}

// Sparse ◇ glyphs drifting in the hero background — same vocabulary
// as /memories' empty state, much sparser (canvas overkill for the
// hero, CSS suffices). Decorative only.
function BackgroundShards() {
  const shards = [
    { top: '12%', left: '8%', size: 'text-xl' },
    { top: '18%', left: '88%', size: 'text-base' },
    { top: '38%', left: '4%', size: 'text-sm' },
    { top: '52%', left: '92%', size: 'text-lg' },
    { top: '74%', left: '12%', size: 'text-base' },
    { top: '82%', left: '78%', size: 'text-xl' },
    { top: '28%', left: '50%', size: 'text-xs' },
  ];
  return (
    <div aria-hidden="true" className="pointer-events-none absolute inset-0 z-0">
      {shards.map((s, i) => (
        <span
          key={`${s.top}-${s.left}`}
          className={`absolute font-display ${s.size} text-ash-700 animate-softGlow`}
          style={{
            top: s.top,
            left: s.left,
            animationDelay: `${i * 0.7}s`,
          }}
        >
          ◇
        </span>
      ))}
    </div>
  );
}

function BeatSection({ beat, index }: { beat: Beat; index: number }) {
  const { t } = useTranslation();
  const ref = useRef<HTMLDivElement | null>(null);
  // Subtle reveal: section opacity rises as it enters the viewport.
  // IntersectionObserver-based, no scroll listener overhead. Each
  // section reveals once.
  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    const obs = new IntersectionObserver(
      (entries) => {
        for (const entry of entries) {
          if (entry.isIntersecting) {
            entry.target.classList.add('is-visible');
            obs.disconnect();
          }
        }
      },
      { threshold: 0.2 },
    );
    obs.observe(el);
    return () => obs.disconnect();
  }, []);

  const reverse = index % 2 === 1;
  const line = t(`landing.beats.${beat.key}.line`);
  const body = t(`landing.beats.${beat.key}.body`);
  const alt = t(`landing.beats.${beat.key}.alt`);
  return (
    <section
      ref={ref}
      className={`landing-reveal flex flex-col items-center px-6 py-6 sm:px-10 sm:py-8 lg:flex-row lg:gap-x-12 lg:px-12 lg:py-12 ${
        reverse ? 'lg:flex-row-reverse' : ''
      }`}
    >
      <figure className="relative w-full max-w-xs flex-shrink-0 lg:w-1/2 lg:max-w-sm">
        <div
          aria-hidden="true"
          className="-inset-8 absolute animate-softGlow rounded-full bg-gradient-radial from-accent/15 via-transparent to-transparent blur-3xl"
        />
        <img
          src={beat.imageSrc}
          alt={alt}
          loading="lazy"
          className="landing-ember-mask relative w-full select-none"
          draggable={false}
        />
      </figure>
      <div className="mt-6 max-w-md text-center lg:mt-0 lg:max-w-xl lg:flex-1 lg:text-left">
        <h2
          className="font-display text-3xl text-ash-50 leading-snug tracking-wide sm:text-4xl lg:text-[2.6rem] lg:leading-[1.15]"
          style={{ fontWeight: 500 }}
        >
          {line}
        </h2>
        {body && (
          <p className="mt-5 font-body text-ash-300 text-base italic leading-relaxed sm:text-lg lg:mt-7 lg:text-xl">
            {body}
          </p>
        )}
      </div>
    </section>
  );
}

function DoorCta() {
  const { t } = useTranslation();
  const titleLines = t('landing.cta.title').split('\n');
  return (
    <section className="flex flex-col items-center px-6 py-12 text-center sm:px-10 sm:py-16 lg:py-20">
      <p className="font-display text-[0.65rem] text-accent-strong uppercase tracking-[0.5em] sm:text-xs lg:text-sm">
        {t('landing.cta.eyebrow')}
      </p>
      <h2
        className="mt-6 max-w-2xl font-display text-ash-50 text-3xl leading-snug tracking-wide sm:text-4xl lg:text-5xl"
        style={{ fontWeight: 500 }}
      >
        {titleLines.map((line, i) => (
          <span key={line}>
            {line}
            {i < titleLines.length - 1 && <br />}
          </span>
        ))}
      </h2>
      <Link
        to="/coming-soon"
        className="mt-12 inline-block rounded-sm border border-accent/60 bg-paper-900/60 px-8 py-4 font-display text-ash-100 text-sm uppercase tracking-[0.34em] transition hover:border-accent-strong/80 hover:bg-paper-700/60 hover:text-ash-50 sm:px-10 sm:tracking-[0.36em] lg:mt-16 lg:px-12 lg:py-5 lg:text-base"
      >
        {t('landing.cta.button')}
      </Link>
      <p className="mt-6 font-body text-ash-500 text-xs italic lg:mt-8 lg:text-sm">
        {t('landing.cta.footnote')}
      </p>
      {/* Secondary CTA next to the primary "Step into the Awakening"
       * button. Same border treatment but lower-weight typography +
       * paler border so it reads as the softer of two paths instead
       * of competing visually with the primary. Critical for Phase A
       * where /awakening is gated — the codex is the only thing a
       * visitor can actually engage with right now without leaving
       * their email. */}
      <Link
        to="/codex"
        className="mt-8 inline-block rounded-sm border border-ash-700/60 px-6 py-3 font-display text-ash-200 text-xs uppercase tracking-[0.32em] transition hover:border-ash-500 hover:text-ash-50 sm:px-8 sm:tracking-[0.34em] lg:mt-10 lg:px-10 lg:py-4 lg:text-sm"
      >
        {t('landing.cta.codexLink')}
      </Link>
    </section>
  );
}

function LandingFooter() {
  const { t } = useTranslation();
  return (
    <footer className="border-paper-800/60 border-t px-6 py-10 text-center font-display text-[0.6rem] text-ash-500 uppercase tracking-[0.34em] sm:px-10">
      <a
        href="https://instagram.com/athrelaris"
        target="_blank"
        rel="noreferrer"
        className="transition hover:text-ash-300"
      >
        @athrelaris
      </a>
      <span aria-hidden="true" className="mx-3 text-ash-700">
        ·
      </span>
      <span>{t('landing.footer.privateBeta')}</span>
      <span aria-hidden="true" className="mx-3 text-ash-700">
        ·
      </span>
      <span>Athrelaris {new Date().getFullYear()}</span>
    </footer>
  );
}
